import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext.jsx";
import { playSound } from "../lib/sound.js";

// Mirrors the level id/title/route in src/pages/Dashboard.jsx's LEVELS array
// (same intentional-duplication convention used in src/data/guides.test.js —
// keeps this a plain data list rather than importing the page).
const LEVELS = [
  { id: "budget-commander", title: "Budget Commander", route: "/games/budget-commander" },
  { id: "time-raid", title: "Time Raid", route: "/games/time-raid" },
  { id: "compound-quest", title: "Compound Quest", route: "/games/compound-quest" },
  { id: "property-tycoon", title: "Property Tycoon", route: "/games/property-tycoon" },
  { id: "bond-bastion", title: "Bond Bastion", route: "/games/bond-bastion" },
  { id: "wildcat-portfolio", title: "Wildcat Portfolio", route: "/games/wildcat-portfolio" },
  { id: "off-the-clock", title: "Off The Clock", route: "/games/off-the-clock" },
  { id: "inner-citadel", title: "The Inner Citadel", route: "/games/inner-citadel" },
  { id: "signal-boost", title: "Signal Boost", route: "/games/signal-boost" },
  { id: "moat-runner", title: "Moat Runner", route: "/games/moat-runner" },
  { id: "the-standoff", title: "The Standoff", route: "/games/the-standoff" },
];

const levelById = Object.fromEntries(LEVELS.map((l) => [l.id, l]));

function classify(challenge, userId) {
  const isChallenger = challenge.challenger_id === userId;
  const myScore = isChallenger ? challenge.challenger_score : challenge.opponent_score;
  const theirScore = isChallenger ? challenge.opponent_score : challenge.challenger_score;
  const theirName = isChallenger ? challenge.opponent_name : challenge.challenger_name;

  let bucket;
  let result = null;
  if (challenge.status === "completed") {
    bucket = "completed";
    if (myScore > theirScore) result = "WIN";
    else if (myScore < theirScore) result = "LOSS";
    else result = "TIE";
  } else if (myScore == null) {
    bucket = "awaiting";
  } else {
    bucket = "waiting";
  }

  return { ...challenge, myScore, theirScore, theirName, bucket, result };
}

export default function Challenges() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [challenges, setChallenges] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const [levelId, setLevelId] = useState(LEVELS[0].id);
  const [opponentId, setOpponentId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  async function loadChallenges() {
    const { data, error } = await supabase.rpc("get_my_challenges");
    if (error) {
      console.error("Failed to load challenges:", error.message);
      setLoadError(error.message);
    } else {
      setLoadError(null);
      setChallenges(data || []);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [challengesRes, candidatesRes] = await Promise.all([
        supabase.rpc("get_my_challenges"),
        supabase.rpc("get_challenge_candidates"),
      ]);
      if (cancelled) return;

      if (challengesRes.error) {
        console.error("Failed to load challenges:", challengesRes.error.message);
        setLoadError(challengesRes.error.message);
      } else {
        setChallenges(challengesRes.data || []);
      }

      if (!candidatesRes.error) {
        setCandidates(candidatesRes.data || []);
        if (candidatesRes.data?.length) setOpponentId(candidatesRes.data[0].id);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSendChallenge(e) {
    e.preventDefault();
    if (!opponentId) return;
    setSending(true);
    setSendError(null);
    setSendSuccess(false);

    const { error } = await supabase.rpc("create_challenge", {
      p_opponent_id: opponentId,
      p_level_id: levelId,
    });

    if (error) {
      console.error("Failed to create challenge:", error.message);
      setSendError(error.message);
    } else {
      playSound("challenge");
      setSendSuccess(true);
      await loadChallenges();
    }
    setSending(false);
  }

  const classified = (challenges || []).map((c) => classify(c, user?.id));
  const awaiting = classified.filter((c) => c.bucket === "awaiting");
  const waiting = classified.filter((c) => c.bucket === "waiting");
  const completed = classified.filter((c) => c.bucket === "completed");

  const resultClasses = {
    WIN: "text-lime",
    LOSS: "text-danger",
    TIE: "text-gold",
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="mb-6">
        <p className="label-pixel mb-2 text-magenta">⚔️ Head To Head</p>
        <h1 className="pixel-heading text-lg text-mist sm:text-2xl">Challenges</h1>
        <p className="mt-3 text-sm text-mist/60">
          Pick a level and a family member — the next time either of you finishes it, the score
          gets paired up here.
        </p>
      </div>

      <form onSubmit={handleSendChallenge} className="panel mb-8 space-y-3 p-5">
        <p className="label-pixel">New Challenge</p>

        <select
          value={levelId}
          onChange={(e) => setLevelId(e.target.value)}
          className="input-pixel"
        >
          {LEVELS.map((l) => (
            <option key={l.id} value={l.id}>
              {l.title}
            </option>
          ))}
        </select>

        <select
          value={opponentId}
          onChange={(e) => setOpponentId(e.target.value)}
          className="input-pixel"
          disabled={candidates.length === 0}
        >
          {candidates.length === 0 && <option value="">No other players yet</option>}
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name || "Player"}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={sending || !opponentId}
          className="btn-magenta w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sending ? "SENDING…" : "SEND CHALLENGE ⚔️"}
        </button>

        {sendError && <p className="text-xs text-danger">Couldn't send that challenge: {sendError}</p>}
        {sendSuccess && <p className="text-xs text-lime">Challenge sent ✅</p>}
      </form>

      {loadError && (
        <div className="panel border-danger p-5 text-sm text-danger">
          Couldn't load your challenges right now: {loadError}
        </div>
      )}

      {!loadError && challenges === null && (
        <div className="panel p-6 text-center">
          <p className="font-display text-[10px] text-cyan">LOADING CHALLENGES…</p>
        </div>
      )}

      {challenges !== null && classified.length === 0 && !loadError && (
        <div className="panel p-6 text-center text-sm text-mist/60">
          No challenges yet — send one above to get started.
        </div>
      )}

      {awaiting.length > 0 && (
        <div className="mb-6">
          <p className="label-pixel mb-2 text-gold">Awaiting Your Play</p>
          <div className="space-y-2">
            {awaiting.map((c) => (
              <div key={c.id} className="panel flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate pixel-heading text-xs text-mist">
                    {levelById[c.level_id]?.title || c.level_id}
                  </p>
                  <p className="text-[10px] text-mist/50">vs. {c.theirName || "Player"}</p>
                </div>
                <button
                  onClick={() => navigate(levelById[c.level_id]?.route || "/")}
                  className="btn-cyan shrink-0 !px-3 !py-2 !text-[8px]"
                >
                  PLAY ▶
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div className="mb-6">
          <p className="label-pixel mb-2 text-cyan">Waiting On Opponent</p>
          <div className="space-y-2">
            {waiting.map((c) => (
              <div key={c.id} className="panel flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate pixel-heading text-xs text-mist">
                    {levelById[c.level_id]?.title || c.level_id}
                  </p>
                  <p className="text-[10px] text-mist/50">vs. {c.theirName || "Player"}</p>
                </div>
                <p className="shrink-0 font-display text-[10px] text-gold">{c.myScore}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {completed.length > 0 && (
        <div className="mb-6">
          <p className="label-pixel mb-2 text-lime">Completed</p>
          <div className="space-y-2">
            {completed.map((c) => (
              <div key={c.id} className="panel flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate pixel-heading text-xs text-mist">
                    {levelById[c.level_id]?.title || c.level_id}
                  </p>
                  <p className="text-[10px] text-mist/50">
                    vs. {c.theirName || "Player"} · {c.myScore} — {c.theirScore}
                  </p>
                </div>
                <p className={`shrink-0 font-display text-[10px] ${resultClasses[c.result]}`}>{c.result}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
